<template>
  <v-row>
    <v-col cols="12" lg="8" class="mx-auto">
      <v-card class="search-container" elevation="2">
        <v-card-text class="pa-8">
          <h5 class="text-h5 mb-2 font-weight-bold">{{ $t('Haku') }}</h5>
          <p class="text-subtitle2 grey--text mb-8">{{ $t('Etsi uutisia, tiedostoja ja sivuja') }}</p>

          <div class="search-input-wrapper mb-8">
            <v-text-field v-model="searchQuery" :label="$t('Kirjoita hakusana')" outlined dense :loading="loading"
              @keyup.enter="performSearch" :append-icon="mdiMagnify" @click:append="performSearch" clearable />
          </div>

          <!-- Empty state -->
          <div v-if="!searched && !loading" class="text-center py-12">
            <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-magnify</v-icon>
            <p class="text-h6 grey--text text-lighten-1">
              {{ $t('Aloita haku kirjoittamalla hakusana') }}
            </p>
          </div>

          <!-- No results -->
          <div v-if="searched && !loading && totalResults === 0" class="text-center py-12">
            <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-magnify-close</v-icon>
            <p class="text-h6 grey--text">
              {{ $t('Ei tuloksia') }}
            </p>
            <p class="text-subtitle2 grey--text mt-2">{{ $t('Yritä eri hakusanaa') }}</p>
          </div>

          <!-- Results -->
          <div v-if="searched && !loading && totalResults > 0">
            <p class="text-subtitle1 font-weight-medium mb-3">
              {{ $t('Löytyi') }} <span class="primary--text font-weight-bold">{{ totalResults }}</span> {{
                $t('tulos(ta)') }}
            </p>
            <v-divider class="mb-8" />

            <!-- News Results -->
            <div v-if="results.news.items.length > 0" class="results-section mb-6">
              <div class="d-flex align-center mb-2">
                <v-icon color="blue" class="mr-2">mdi-newspaper</v-icon>
                <span class="text-h6 font-weight-medium pr-1">{{ $t('Uutiset') }}</span>
                ({{ results.news.items.length }})
              </div>
              <v-list two-line>
                <template v-for="(item, index) in results.news.items">
                  <v-list-item :key="`news-${item.id}`" @click="viewNews(item.id)">
                    <v-list-item-icon>
                      <v-icon color="blue">{{ mdiFormatQuoteClose }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.published_date">
                        {{ formatDate(item.published_date) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-icon>
                      <v-icon color="blue">mdi-chevron-right</v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                  <v-divider v-if="index < results.news.items.length - 1" :key="`news-divider-${item.id}`" inset />
                </template>
              </v-list>
              <div v-if="results.news.hasMore" class="text-center mt-4">
                <v-btn text color="primary" :loading="loadingMore.news" @click="loadMore('news')">
                  {{ $t('Näytä lisää') }}
                </v-btn>
              </div>
            </div>

            <!-- Resources Results -->
            <div v-if="tenant.settings.resources_enabled && results.resources.items.length > 0"
              class="results-section mb-6">
              <div class="d-flex align-center mb-2">
                <v-icon color="green" class="mr-2">mdi-file-document</v-icon>
                <span class="text-h6 font-weight-medium pr-1">{{ $t('Tiedostot') }}</span>
                ({{ results.resources.items.length }})
              </div>
              <v-list two-line>
                <template v-for="(item, index) in results.resources.items">
                  <v-list-item :key="`resource-${item.id}`" @click="viewResource(item)">
                    <v-list-item-icon>
                      <v-icon color="green">{{ mdiAttachment }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.published_date">
                        {{ formatDate(item.published_date) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-icon>
                      <v-icon color="green">mdi-chevron-right</v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                  <v-divider v-if="index < results.resources.items.length - 1" :key="`resource-divider-${item.id}`"
                    inset />
                </template>
              </v-list>
              <div v-if="results.resources.hasMore" class="text-center mt-4">
                <v-btn text color="primary" :loading="loadingMore.resources" @click="loadMore('resources')">
                  {{ $t('Näytä lisää') }}
                </v-btn>
              </div>
            </div>

            <!-- Pages Results -->
            <div v-if="results.pages.items.length > 0" class="results-section mb-6">
              <div class="d-flex align-center mb-2">
                <v-icon color="orange" class="mr-2">mdi-file-multiple</v-icon>
                <span class="text-h6 font-weight-medium pr-1">{{ $t('Sivut') }}</span>
                ({{ results.pages.items.length }})
              </div>
              <v-list two-line>
                <template v-for="(item, index) in results.pages.items">
                  <v-list-item :key="`page-${item.id}`" @click="viewPage(item.id, item.extra && item.extra.category)">
                    <v-list-item-icon>
                      <v-icon color="orange">{{ mdiInformationOutline }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.published_date">
                        {{ formatDate(item.published_date) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-icon>
                      <v-icon color="orange">mdi-chevron-right</v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                  <v-divider v-if="index < results.pages.items.length - 1" :key="`page-divider-${item.id}`" inset />
                </template>
              </v-list>
              <div v-if="results.pages.hasMore" class="text-center mt-4">
                <v-btn text color="primary" :loading="loadingMore.pages" @click="loadMore('pages')">
                  {{ $t('Näytä lisää') }}
                </v-btn>
              </div>
            </div>

            <!-- FAQ Results -->
            <div v-if="results.faqs.items.length > 0" class="results-section">
              <div class="d-flex align-center mb-2">
                <span class="text-h6 font-weight-medium pr-1">{{ $t('Kysymykset & vastaukset') }}</span>
                ({{ results.faqs.items.length }})
              </div>
              <v-list three-line>
                <template v-for="(item, index) in results.faqs.items">
                  <v-list-item :key="`faq-${item.id}`" @click="toggleFaq(item.id)">
                    <v-list-item-icon>
                      <v-icon color="purple">{{ mdiHelpCircleOutline }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle v-if="!isFaqExpanded(item.id)">
                        {{ faqAnswerPreview(item) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-icon>
                      <v-icon color="purple">{{ isFaqExpanded(item.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'
                        }}</v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                  <v-expand-transition :key="`faq-expand-${item.id}`">
                    <div v-if="isFaqExpanded(item.id)" class="faq-answer pl-14 pr-4 pb-4"
                      v-html="markdownToHtml(item.extra && item.extra.answer)"></div>
                  </v-expand-transition>
                  <v-divider v-if="index < results.faqs.items.length - 1" :key="`faq-divider-${item.id}`" inset />
                </template>
              </v-list>
              <div v-if="results.faqs.hasMore" class="text-center mt-4">
                <v-btn text color="primary" :loading="loadingMore.faqs" @click="loadMore('faqs')">
                  {{ $t('Näytä lisää') }}
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-dialog v-model="resourceDialog" max-width="500">
        <v-card v-if="selectedResource">
          <v-card-title class="d-flex align-center">
            {{ selectedResource.title }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4">
            <p v-if="selectedResource.extra && selectedResource.extra.description" class="mb-0">
              {{ selectedResource.extra.description }}
            </p>
            <p v-else class="grey--text mb-0">
              {{ $t('Ei kuvausta') }}
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="resourceDialog = false">{{ $t('Sulje') }}</v-btn>
            <v-btn color="primary" :loading="downloadingResource" @click="openResource">
              <v-icon left small>{{ isResourceLink(selectedResource) ? mdiOpenInNew : mdiDownload }}</v-icon>
              {{ isResourceLink(selectedResource) ? $t('Avaa') : $t('Lataa') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="showError" color="error">
        {{ errorMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="showError = false">{{ $t('Sulje') }}</v-btn>
        </template>
      </v-snackbar>
    </v-col>
  </v-row>
</template>

<script>
import { ref, computed, onMounted, watch } from "@vue/composition-api";
import { globalSearch } from '@/api/search.api';
import { useRouter } from "@core/utils";
import { mdiMagnify, mdiFormatQuoteClose, mdiAttachment, mdiInformationOutline, mdiHelpCircleOutline, mdiOpenInNew, mdiDownload } from "@mdi/js";
import api from "@/api";
import download from "downloadjs";
import useAuth from "@/useAuth";
import useMarkdown from "@/@core/utils/useMarkdown";
import { useTranslations } from "@core/libs/i18n";

const MIN_QUERY_LENGTH = 3;

const emptyResults = () => ({
  news: { items: [], hasMore: false },
  resources: { items: [], hasMore: false },
  pages: { items: [], hasMore: false },
  faqs: { items: [], hasMore: false },
});

export default {
  setup(props) {

    const { tenant } = useAuth();
    const { router, route } = useRouter();
    const { markdownToHtml } = useMarkdown();
    const { t } = useTranslations();
    const searchQuery = ref('');
    const results = ref(emptyResults());
    const loading = ref(false);
    const loadingMore = ref({ news: false, resources: false, pages: false, faqs: false });
    const searched = ref(false);
    const showError = ref(false);
    const errorMessage = ref('');
    const resourceDialog = ref(false);
    const selectedResource = ref(null);
    const downloadingResource = ref(false);
    const expandedFaqIds = ref(new Set());

    const totalResults = computed(() => {
      return (
        results.value.news.items.length +
        results.value.resources.items.length +
        results.value.pages.items.length +
        results.value.faqs.items.length
      );
    });

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fi-FI', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    };

    const runSearch = async (query) => {
      if (loading.value) return;

      loading.value = true;
      searched.value = true;

      try {
        const response = await globalSearch(query);
        results.value = {
          news: {
            items: response.data.news.results,
            hasMore: response.data.news.has_more,
          },
          resources: {
            items: response.data.resources.results,
            hasMore: response.data.resources.has_more,
          },
          pages: {
            items: response.data.pages.results,
            hasMore: response.data.pages.has_more,
          },
          faqs: {
            items: response.data.faqs.results,
            hasMore: response.data.faqs.has_more,
          },
        };
      } catch (error) {
        console.error('Search error:', error);
        errorMessage.value = 'Haku epäonnistui. Yritä uudelleen.';
        showError.value = true;
        results.value = emptyResults();
      } finally {
        loading.value = false;
      }
    };

    const performSearch = async () => {
      const query = searchQuery.value.trim();
      if (!query) {
        errorMessage.value = t('Kirjoita hakusana');
        showError.value = true;
        return;
      }
      if (query.length < MIN_QUERY_LENGTH) {
        errorMessage.value = t(`Hakusanan tulee olla vähintään ${MIN_QUERY_LENGTH} merkkiä pitkä`);
        showError.value = true;
        return;
      }

      await runSearch(query);
      router.replace({ query: { q: query } }).catch(() => { });
    };

    // Keep the search page in sync with the URL so the browser back/forward
    // buttons move between previous searches instead of leaving the page.
    onMounted(() => {
      const initialQuery = route.value.query.q;
      if (initialQuery) {
        searchQuery.value = initialQuery;
        if (initialQuery.length >= MIN_QUERY_LENGTH) {
          runSearch(initialQuery);
        }
      }
    });

    watch(
      () => route.value.query.q,
      (newQuery) => {
        const query = newQuery || '';
        if (query === searchQuery.value) return;

        searchQuery.value = query;
        if (query && query.length >= MIN_QUERY_LENGTH) {
          runSearch(query);
        } else {
          searched.value = false;
          results.value = emptyResults();
        }
      }
    );

    const loadMore = async (type) => {
      loadingMore.value[type] = true;

      try {
        const offset = results.value[type].items.length;
        const response = await globalSearch(searchQuery.value, type, offset);
        results.value[type].items.push(...response.data[type].results);
        results.value[type].hasMore = response.data[type].has_more;
      } catch (error) {
        console.error('Load more error:', error);
        errorMessage.value = 'Lisätulosten lataus epäonnistui.';
        showError.value = true;
      } finally {
        loadingMore.value[type] = false;
      }
    };

    const viewNews = (id) => {
      router.push({ name: 'news', params: { id } });
    };

    const isResourceLink = (item) => !!(item && item.extra && item.extra.url);

    const viewResource = (item) => {
      selectedResource.value = item;
      resourceDialog.value = true;
    };

    const openResource = async () => {
      const item = selectedResource.value;

      if (!item) return;

      if (isResourceLink(item)) {
        window.open(item.extra.url, '_blank');
        resourceDialog.value = false;
        return;
      }

      downloadingResource.value = true;
      try {
        const response = await api.downloadResource(item.id);
        if (response.ok) {
          const filename = (item.extra && item.extra.filename) || item.title;
          download(response.data, filename);
          resourceDialog.value = false;
        } else {
          errorMessage.value = 'Tiedoston lataus epäonnistui.';
          showError.value = true;
        }
      } catch (error) {
        console.error('Resource download error:', error);
        errorMessage.value = 'Tiedoston lataus epäonnistui.';
        showError.value = true;
      } finally {
        downloadingResource.value = false;
      }
    };

    const viewPage = (id, category) => {
      router.push({ name: 'company-page', params: { id }, query: { category } });
    };

    const FAQ_PREVIEW_LENGTH = 160;

    const faqAnswerPreview = (item) => {
      const answer = item.extra && item.extra.answer;
      if (!answer) return '';

      const div = document.createElement('div');
      div.innerHTML = markdownToHtml(answer);
      const text = (div.textContent || '').trim();

      return text.length > FAQ_PREVIEW_LENGTH ? `${text.slice(0, FAQ_PREVIEW_LENGTH)}…` : text;
    };

    const isFaqExpanded = (id) => expandedFaqIds.value.has(id);

    const toggleFaq = (id) => {
      const next = new Set(expandedFaqIds.value);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      expandedFaqIds.value = next;
    };

    return {
      tenant,
      searchQuery,
      results,
      loading,
      loadingMore,
      searched,
      showError,
      errorMessage,
      resourceDialog,
      selectedResource,
      downloadingResource,
      totalResults,
      performSearch,
      loadMore,
      viewNews,
      viewResource,
      viewPage,
      faqAnswerPreview,
      isFaqExpanded,
      toggleFaq,
      markdownToHtml,
      openResource,
      isResourceLink,
      formatDate,
      mdiMagnify,
      mdiFormatQuoteClose,
      mdiAttachment,
      mdiInformationOutline,
      mdiHelpCircleOutline,
      mdiOpenInNew,
      mdiDownload,
    };
  },
};
</script>

<style scoped>
.search-container {
  border-radius: 12px;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
}

.search-input {
  flex: 1;
}

.results-section {
  animation: fadeIn 0.3s ease-in;
}

.faq-answer img {
  max-width: 100%;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
